import type { ChannelClient } from '@hibro/platform-core';
import { NativeContentsSuppressionProvider } from '@hibro/platform-renderer/native-contents';
import {
  CONTRIBUTIONS_CHANGED_EVENT,
  type IModuleService,
  type ResolvedContributions,
  type ResolvedView,
} from '@hibro/shared';
import { UiClientConfigProvider } from '@hibro/ui-client';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  CustomHeader,
  type WindowControlsBridge,
} from '../components/custom-header/custom-header';
import {
  ViewEventBusContext,
  type ViewEventSubscribe,
} from '../event-bus/event-bus';
import {
  NavigationContext,
  type NavigationSource,
} from '../navigation/navigation';
import { type LoadUi, UiRegistry } from '../runtime/ui-registry';
import { resolveOverlayPortalContainer } from '../theme/portal-container';
import { OverlayProvider } from './overlay-host';
import { SurfaceView } from './surface-host';
import { SurfaceHostedInProvider } from './surface-hosted-in';

export interface ModuleShellHostProps {
  shellId: string;
  moduleService: IModuleService;
  uiLoaders: Map<string, LoadUi>;
  client?: ChannelClient;
  navigation?: NavigationSource;
  windowControlsBridge?: WindowControlsBridge;
  onError?: (message: string) => void;
}

/**
 * Generic top-level host for a module-contributed shell. The platform owns the
 * shared window chrome and contribution lifecycle; the module owns the body.
 */
export function ModuleShellHost({
  shellId,
  moduleService,
  uiLoaders,
  client,
  navigation,
  windowControlsBridge,
  onError,
}: ModuleShellHostProps): ReactNode {
  const [shells, setShells] = useState<ResolvedContributions['shells']>([]);
  const [views, setViews] = useState<ResolvedView[]>([]);
  const [runtimeUiEntries, setRuntimeUiEntries] = useState<
    ResolvedContributions['runtimeUiEntries']
  >([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string>();
  const [overlayOpen, setOverlayOpen] = useState(false);
  const disposedRef = useRef(false);
  const refreshGenerationRef = useRef(0);
  const invalidateRefresh = useCallback((): void => {
    refreshGenerationRef.current++;
  }, []);

  const refresh = useCallback(() => {
    const generation = ++refreshGenerationRef.current;
    void moduleService
      .listContributions()
      .then((contributions) => {
        if (
          disposedRef.current ||
          generation !== refreshGenerationRef.current
        ) {
          return;
        }
        setShells(contributions.shells);
        setViews(contributions.views);
        setRuntimeUiEntries(contributions.runtimeUiEntries);
        setLoaded(true);
        setError(undefined);
      })
      .catch((reason: unknown) => {
        if (
          disposedRef.current ||
          generation !== refreshGenerationRef.current
        ) {
          return;
        }
        const message = `Shell contributions could not be loaded: ${
          reason instanceof Error ? reason.message : String(reason)
        }`;
        setError(message);
        onError?.(message);
      });
  }, [moduleService, onError]);

  useEffect(() => {
    disposedRef.current = false;
    refresh();
    return () => {
      disposedRef.current = true;
      invalidateRefresh();
    };
  }, [invalidateRefresh, refresh]);

  useEffect(() => {
    if (!client) {
      return;
    }
    return client.listen(CONTRIBUTIONS_CHANGED_EVENT, refresh);
  }, [client, refresh]);

  const shell = useMemo(
    () => shells.find((candidate) => candidate.id === shellId) ?? null,
    [shellId, shells],
  );
  const view = useMemo(
    () =>
      shell
        ? (views.find(
            (candidate) =>
              candidate.id === shell.viewId &&
              candidate.moduleId === shell.moduleId,
          ) ?? null)
        : null,
    [shell, views],
  );
  const registry = useMemo(
    () => new UiRegistry(uiLoaders, views, undefined, runtimeUiEntries),
    [runtimeUiEntries, uiLoaders, views],
  );
  const onEvent = useMemo<ViewEventSubscribe>(
    () =>
      client
        ? (event, handler) => client.listen(event, handler)
        : () => () => undefined,
    [client],
  );
  const message =
    error ??
    (loaded && !shell
      ? `Shell "${shellId}" is not registered.`
      : loaded && shell && !view
        ? `Shell "${shellId}" does not resolve to a view.`
        : undefined);

  return (
    <SurfaceHostedInProvider value="detached">
      <UiClientConfigProvider portalContainer={resolveOverlayPortalContainer()}>
        <ViewEventBusContext.Provider value={onEvent}>
          <NavigationContext.Provider value={navigation}>
            <NativeContentsSuppressionProvider suppressed={overlayOpen}>
              <OverlayProvider
                client={client}
                views={views}
                registry={registry}
                moduleService={moduleService}
                onOpenChange={setOverlayOpen}
                presentWindow={
                  windowControlsBridge
                    ? () => windowControlsBridge.present()
                    : undefined
                }
              >
                <div className="flex h-full w-full flex-col overflow-hidden bg-card font-[system-ui,sans-serif] text-foreground">
                  <CustomHeader bridge={windowControlsBridge} />
                  <div className="flex min-h-0 flex-1">
                    {view ? (
                      <SurfaceView
                        viewId={view.id}
                        moduleId={view.moduleId}
                        registry={registry}
                        moduleService={moduleService}
                        kind={view.kind}
                        url={view.url}
                        subscribeContributionsChanged={
                          client
                            ? (listener) =>
                                client.listen(
                                  CONTRIBUTIONS_CHANGED_EVENT,
                                  listener,
                                )
                            : undefined
                        }
                      />
                    ) : message ? (
                      <div className="m-auto max-w-xl px-6 text-center text-sm text-muted-foreground">
                        {message}
                      </div>
                    ) : null}
                  </div>
                </div>
              </OverlayProvider>
            </NativeContentsSuppressionProvider>
          </NavigationContext.Provider>
        </ViewEventBusContext.Provider>
      </UiClientConfigProvider>
    </SurfaceHostedInProvider>
  );
}
